import { AlertTriangle, Crown, Eye, Loader2, RefreshCw, ShieldAlert, Trash2, Undo2, UserX, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { deleteAdminCheater, fetchAdminCheaterLedger, listAdminCheaters, runAdminReconcileNow, unflagAdminUser } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';

/**
 * Patch v18.0 — Cheaters (Fraud & Reconciliation) Admin Subpage.
 *
 * God-Admin only (backend endpoints all return 403 to sub-admins; sidebar
 * also hides the nav entry via GOD_ONLY_SUBPAGES in AdminSidebar.jsx).
 *
 * Displays every user whose ``is_flagged`` field is true. The primary
 * flagging trigger is the reconciliation cron in
 * ``services/fraud_detection.py`` which detects direct MongoDB writes to
 * ``users.purchase_balance`` / ``withdrawal_balance`` bypassing the
 * ledger. A God Admin can also manually flag or unflag a user from here.
 *
 * Actions per row:
 *   - View Ledger : opens a modal with the user's recent ledger history +
 *                   drift breakdown so the operator can inspect the
 *                   evidence before deciding what to do.
 *   - Unflag      : clears the flag AND snapshots the current raw_drift
 *                   into ``false_positive_baseline`` so the cron does
 *                   not immediately re-flag them for the same drift.
 *   - Delete      : hard-deletes the account (soft under the hood —
 *                   positions marked admin_purged=true, subpacks
 *                   deactivated, pending withdrawals voided, balances
 *                   zeroed, is_deleted_at set). Requires the operator
 *                   to retype the exact username to confirm.
 */

const fmt$ = (raw) => {
  const n = Number(raw ?? 0);
  const sign = n < 0 ? '-' : '+';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const fmtAmount = (raw) => {
  const n = Number(raw ?? 0);
  return `$${n.toFixed(2)}`;
};

const shortTs = (iso) => {
  if (!iso) return '—';
  return String(iso).slice(0, 19).replace('T', ' ');
};

/** Ledger rows per page in the View Ledger modal. */
const LEDGER_PAGE_SIZE = 200;

const CheatersSubpage = () => {
  const [rows, setRows] = useState([]);
  // Server-paged (50 / page, newest flag first); the dates filter the flag date.
  const [page, setPage] = useState(1);
  const [paging, setPaging] = useState({ page: 1, total: 0, total_pages: 1 });
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const appliedDates = useDebouncedValue(dates);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  // Modals
  const [ledgerUser, setLedgerUser] = useState(null);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerDates, setLedgerDates] = useState(EMPTY_DATE_RANGE);
  const appliedLedgerDates = useDebouncedValue(ledgerDates);
  const [ledgerData, setLedgerData] = useState(null); // { items, drift_summary, page, total, total_pages }
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const ledgerModal = ledgerUser ? { user: ledgerUser, items: ledgerData?.items || [], summary: ledgerData?.drift_summary || null } : null;
  const [deleteModal, setDeleteModal] = useState(null); // {user}
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteWorking, setDeleteWorking] = useState(false);
  const [unflagWorking, setUnflagWorking] = useState(null); // user_id

  const load = useCallback(async () => {
    try {
      const data = await listAdminCheaters(page, dateRangeParams(appliedDates));
      setRows(data.items || []);
      setPaging({ page: data.page || page, total: data.total ?? 0, total_pages: data.total_pages || 1 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to load cheaters list');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, appliedDates]);

  useEffect(() => {
    load();
  }, [load]);

  // The open ledger's page: reloads when the member, page or date range changes.
  useEffect(() => {
    if (!ledgerUser) return undefined;
    let alive = true;
    setLedgerLoading(true);
    (async () => {
      try {
        const data = await fetchAdminCheaterLedger(ledgerUser.user_id, {
          page: ledgerPage,
          limit: LEDGER_PAGE_SIZE,
          dates: dateRangeParams(appliedLedgerDates),
        });
        if (alive) setLedgerData(data);
      } catch (err) {
        if (!alive) return;
        toast.error(err?.response?.data?.detail || err?.message || 'Failed to load ledger');
        setLedgerUser(null);
      } finally {
        if (alive) setLedgerLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [ledgerUser, ledgerPage, appliedLedgerDates]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleRescan = async () => {
    setRescanning(true);
    try {
      const summary = await runAdminReconcileNow();
      const newFlags = Number(summary?.flagged_new || 0);
      toast.success(
        newFlags > 0
          ? `Rescan complete — ${newFlags} new account${newFlags === 1 ? '' : 's'} flagged.`
          : `Rescan complete — no new accounts flagged. Scanned ${summary?.scanned ?? '?'} users.`,
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Rescan failed');
    } finally {
      setRescanning(false);
    }
  };

  const handleUnflag = async (row) => {
    if (!window.confirm(`Unflag ${row.username}? Their withdrawal block will be lifted immediately.`)) return;
    setUnflagWorking(row.user_id);
    try {
      const reason = window.prompt('Optional reason for unflagging (stored on the audit trail):', 'admin_reviewed_false_positive');
      await unflagAdminUser(row.user_id, reason || 'admin_reviewed_false_positive');
      toast.success(`${row.username} unflagged. Baseline snapshot saved.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Unflag failed');
    } finally {
      setUnflagWorking(null);
    }
  };

  const openLedger = (row) => {
    setLedgerData(null);
    setLedgerPage(1);
    setLedgerDates(EMPTY_DATE_RANGE);
    setLedgerUser(row);
  };

  const openDelete = (row) => {
    setDeleteModal({ user: row });
    setDeleteConfirmText('');
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    const { user } = deleteModal;
    if (deleteConfirmText !== user.username) {
      toast.error(`Type "${user.username}" exactly to confirm.`);
      return;
    }
    setDeleteWorking(true);
    try {
      const summary = await deleteAdminCheater(user.user_id, deleteConfirmText);
      toast.success(
        `${user.username} deleted. ${summary.positions_purged} positions, ${summary.subpacks_purged} subpacks purged; ${summary.withdrawals_voided} pending payouts voided.`,
      );
      setDeleteModal(null);
      setDeleteConfirmText('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || 'Delete failed');
    } finally {
      setDeleteWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[320px] flex items-center justify-center" data-testid="cheaters-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FFC20E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cheaters-subpage">
      {/* Header */}
      <div className="sc-glass rounded-2xl p-5 border border-[rgba(255,182,18,0.14)]">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(248,113,113,0.14)',
                border: '1px solid rgba(248,113,113,0.42)',
              }}
            >
              <UserX className="w-5 h-5 text-[#fca5a5]" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display font-bold text-[18px]">
                Cheaters <span className="text-[var(--sc-text-mute)] font-normal">&amp; Reconciliation</span>
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]">
                Accounts flagged by drift detection or manual review — {paging.total} record{paging.total === 1 ? '' : 's'}
              </div>
            </div>
          </div>
          <div className="md:ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              className="sc-btn-outline text-xs flex items-center gap-1.5"
              onClick={handleRescan}
              disabled={rescanning}
              data-testid="cheaters-rescan-btn"
              title="Trigger an on-demand reconciliation scan (in addition to the scheduled 5-min cron)"
            >
              {rescanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
              Run Scan Now
            </button>
            <button
              type="button"
              className="sc-btn-outline text-xs flex items-center gap-1.5"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="cheaters-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <InfoTile
            label="Detection Mechanism"
            value="Reconciliation Cron (5 min)"
            hint="Auto-flags accounts whose mutable balance drifts from the ledger-derived value by more than the configured threshold."
          />
          <InfoTile
            label="Withdrawal Gate"
            value="Blocked While Flagged"
            hint="wallet.withdraw() refuses payout when is_flagged=true. Fail-open on DB error so a Mongo blip cannot lock out every member."
          />
          <InfoTile
            label="Delete Semantics"
            value="Soft: positions purged, subpacks deactivated"
            hint="Ledger rows are NEVER touched. User document is marked is_deleted_at (not physically removed) so forensic history is preserved."
          />
        </div>
      </div>

      {/* Table */}
      <div className="sc-glass rounded-2xl p-4 border border-[rgba(255,182,18,0.10)] overflow-x-auto">
        <div className="mb-3 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={dates.date_from}
            dateTo={dates.date_to}
            onChange={(range) => {
              setDates(range);
              setPage(1);
            }}
            fromLabel="Flagged From"
            toLabel="Flagged To"
            testIdPrefix="cheaters-date"
          />
        </div>
        <table className="w-full text-sm" data-testid="cheaters-table">
          <thead className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
            <tr>
              <th className="py-2 px-3">User</th>
              <th className="py-2 px-3">Flagged At</th>
              <th className="py-2 px-3">Reason</th>
              <th className="py-2 px-3 text-right">Live Drift</th>
              <th className="py-2 px-3 text-right">Deposits</th>
              <th className="py-2 px-3 text-right">Withdrawn</th>
              <th className="py-2 px-3 text-right">Positions</th>
              <th className="py-2 px-3 text-right">Subpacks</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[rgba(255,182,18,0.06)]">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-[var(--sc-text-mute)]" data-testid="cheaters-empty">
                  No flagged accounts. The reconciliation cron will surface any new drift within 5 minutes.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.user_id} data-testid={`cheaters-row-${row.user_id}`}>
                  <td className="py-3 px-3">
                    <div className="font-semibold flex items-center gap-1.5">
                      {row.username || row.user_id}
                      {row.upline_user_id === '0' ? <Crown className="w-3 h-3 text-[#FFE08A]" title="Root / orphan pool" /> : null}
                    </div>
                    <div className="text-[10px] text-[var(--sc-text-mute)]">{row.user_id}</div>
                    {row.email ? <div className="text-[10px] text-[var(--sc-text-mute)] truncate max-w-[220px]">{row.email}</div> : null}
                  </td>
                  <td className="py-3 px-3 text-xs sc-tabular text-[var(--sc-text-dim)]">{shortTs(row.flagged_at)}</td>
                  <td className="py-3 px-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase"
                      style={{
                        background: 'rgba(248,113,113,0.14)',
                        border: '1px solid rgba(248,113,113,0.42)',
                        color: '#fca5a5',
                      }}
                    >
                      {row.flagged_reason || 'flagged'}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right sc-tabular text-[#fca5a5] font-semibold" data-testid={`cheaters-drift-${row.user_id}`}>
                    {fmt$(row.live_net_drift)}
                  </td>
                  <td className="py-3 px-3 text-right sc-tabular text-[var(--sc-text-dim)]">{fmtAmount(row.lifetime_deposits_usd)}</td>
                  <td className="py-3 px-3 text-right sc-tabular text-[var(--sc-text-dim)]">{fmtAmount(row.lifetime_withdrawals_usd)}</td>
                  <td className="py-3 px-3 text-right sc-tabular">{row.active_positions}</td>
                  <td className="py-3 px-3 text-right sc-tabular">{row.active_subpacks}</td>
                  <td className="py-3 px-3">
                    {row.is_deleted_at ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.18em]"
                        style={{
                          background: 'rgba(148,163,184,0.12)',
                          border: '1px solid rgba(148,163,184,0.42)',
                          color: '#cbd5e1',
                        }}
                      >
                        Purged
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.18em]"
                        style={{
                          background: 'rgba(255,194,14,0.14)',
                          border: '1px solid rgba(255,194,14,0.42)',
                          color: '#FFE08A',
                        }}
                      >
                        Flagged
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        type="button"
                        className="sc-btn-outline text-[10px] px-2 py-1 flex items-center gap-1"
                        onClick={() => openLedger(row)}
                        data-testid={`cheaters-view-${row.user_id}`}
                        title="Inspect ledger + drift breakdown"
                      >
                        <Eye className="w-3 h-3" />
                        Ledger
                      </button>
                      <button
                        type="button"
                        className="sc-btn-outline text-[10px] px-2 py-1 flex items-center gap-1"
                        onClick={() => handleUnflag(row)}
                        disabled={unflagWorking === row.user_id || Boolean(row.is_deleted_at)}
                        data-testid={`cheaters-unflag-${row.user_id}`}
                        title="Clear the flag AND snapshot current drift as baseline"
                      >
                        {unflagWorking === row.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                        Unflag
                      </button>
                      <button
                        type="button"
                        className="text-[10px] px-2 py-1 rounded-md font-bold flex items-center gap-1 transition"
                        style={
                          row.is_deleted_at
                            ? {
                                background: 'rgba(148,163,184,0.10)',
                                border: '1px solid rgba(148,163,184,0.32)',
                                color: '#94a3b8',
                                cursor: 'not-allowed',
                              }
                            : {
                                background: 'rgba(248,113,113,0.12)',
                                border: '1px solid rgba(248,113,113,0.55)',
                                color: '#fca5a5',
                              }
                        }
                        onClick={() => openDelete(row)}
                        disabled={Boolean(row.is_deleted_at)}
                        data-testid={`cheaters-delete-${row.user_id}`}
                        title={row.is_deleted_at ? 'Already purged' : 'Purge positions, subpacks, void pending payouts, zero balances'}
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <AdminPagination page={paging.page} totalPages={paging.total_pages} total={paging.total} onChange={setPage} />
      </div>

      {/* Ledger Modal */}
      {ledgerModal ? (
        <div
          className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          data-testid="cheaters-ledger-modal"
        >
          <div className="sc-glass rounded-2xl p-5 max-w-4xl w-full max-h-[85vh] flex flex-col border border-[rgba(255,182,18,0.24)]">
            <div className="flex items-center gap-3 pb-3 border-b border-[rgba(255,182,18,0.10)]">
              <div className="font-display font-bold text-[16px]">
                Ledger — <span className="sc-gold-text">{ledgerModal.user.username}</span>
              </div>
              <button
                type="button"
                className="ml-auto text-[var(--sc-text-mute)] hover:text-[#FFC20E]"
                onClick={() => setLedgerUser(null)}
                data-testid="cheaters-ledger-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {ledgerModal.summary ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                <InfoTile label="Actual Balance" value={fmtAmount(ledgerModal.summary.actual_total)} />
                <InfoTile label="Ledger-Derived" value={fmtAmount(ledgerModal.summary.ledger_total)} />
                <InfoTile label="Raw Drift" value={fmt$(ledgerModal.summary.raw_drift)} accent="rose" />
                <InfoTile label="FP Baseline" value={fmt$(ledgerModal.summary.false_positive_baseline)} />
                <InfoTile label="Net Drift" value={fmt$(ledgerModal.summary.net_drift)} accent="rose" />
              </div>
            ) : null}
            <div className="mt-3 flex justify-end">
              <AdminDateRange
                wrap
                dateFrom={ledgerDates.date_from}
                dateTo={ledgerDates.date_to}
                onChange={(range) => {
                  setLedgerDates(range);
                  setLedgerPage(1);
                }}
                testIdPrefix="cheaters-ledger-date"
              />
            </div>
            <div className="mt-3 overflow-y-auto flex-1">
              {ledgerLoading ? (
                <div className="py-8 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FFC20E]" />
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] sticky top-0 bg-[rgba(8,8,9,0.94)]">
                    <tr>
                      <th className="py-2 px-2">Timestamp</th>
                      <th className="py-2 px-2">Type</th>
                      <th className="py-2 px-2 text-right">Amount</th>
                      <th className="py-2 px-2">Metadata</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(255,182,18,0.06)]">
                    {ledgerModal.items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-[var(--sc-text-mute)]">
                          No ledger rows for this user.
                        </td>
                      </tr>
                    ) : (
                      ledgerModal.items.map((row) => (
                        <tr key={row.transaction_id}>
                          <td className="py-1.5 px-2 sc-tabular text-[var(--sc-text-dim)]">{shortTs(row.timestamp)}</td>
                          <td className="py-1.5 px-2">
                            <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#FFE08A]">{row.type}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right sc-tabular text-[#FFC20E]">{fmt$(row.amount)}</td>
                          <td className="py-1.5 px-2 text-[10px] font-mono text-[var(--sc-text-mute)] truncate max-w-[380px]">
                            {row.metadata ? JSON.stringify(row.metadata) : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <AdminPagination
              page={ledgerData?.page || ledgerPage}
              totalPages={ledgerData?.total_pages || 1}
              total={ledgerData?.total ?? 0}
              pageSize={ledgerData?.page_size || LEDGER_PAGE_SIZE}
              onChange={setLedgerPage}
            />
          </div>
        </div>
      ) : null}

      {/* Delete Modal */}
      {deleteModal ? (
        <div
          className="fixed inset-0 z-[310] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          data-testid="cheaters-delete-modal"
        >
          <div className="sc-glass rounded-2xl p-5 max-w-lg w-full border border-[rgba(248,113,113,0.55)]">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[#fca5a5]" />
              <div className="font-display font-bold text-[16px]">Delete Cheater Account</div>
              <button
                type="button"
                className="ml-auto text-[var(--sc-text-mute)] hover:text-[#FFC20E]"
                onClick={() => setDeleteModal(null)}
                disabled={deleteWorking}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm mt-3 text-[var(--sc-text-dim)]">This will:</p>
            <ul className="text-xs mt-2 space-y-1 pl-4 list-disc text-[var(--sc-text-dim)]">
              <li>
                Mark all {deleteModal.user.active_positions} active position(s) as <code>admin_purged</code>
              </li>
              <li>Deactivate all {deleteModal.user.active_subpacks} active sub pack(s)</li>
              <li>Void all pending withdrawals for this account</li>
              <li>Zero out purchase / withdrawal / auto-repurchase balances</li>
              <li>
                Mark the user document <code>is_deleted_at</code> (not physically removed)
              </li>
              <li className="text-[#FFE08A]">Ledger rows are NEVER touched — audit trail preserved</li>
            </ul>

            <p className="text-xs mt-4 text-[var(--sc-text-mute)]">
              Retype the exact username to confirm:
              <br />
              <span className="font-mono text-[#FFC20E] font-bold">{deleteModal.user.username}</span>
            </p>
            <input
              type="text"
              className="sc-input w-full mt-2 font-mono"
              placeholder="Type the username here"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={deleteWorking}
              data-testid="cheaters-delete-confirm-input"
              // biome-ignore lint/a11y/noAutofocus: focus the confirmation field of the delete dialog the admin just opened
              autoFocus
            />

            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="sc-btn-outline text-xs" onClick={() => setDeleteModal(null)} disabled={deleteWorking}>
                Cancel
              </button>
              <button
                type="button"
                className="text-xs px-3 py-2 rounded-md font-bold flex items-center gap-1.5"
                style={{
                  background: 'rgba(248,113,113,0.20)',
                  border: '1px solid rgba(248,113,113,0.65)',
                  color: '#fca5a5',
                  opacity: deleteConfirmText === deleteModal.user.username && !deleteWorking ? 1 : 0.5,
                  cursor: deleteConfirmText === deleteModal.user.username && !deleteWorking ? 'pointer' : 'not-allowed',
                }}
                onClick={handleDelete}
                disabled={deleteConfirmText !== deleteModal.user.username || deleteWorking}
                data-testid="cheaters-delete-confirm-btn"
              >
                {deleteWorking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Permanently Purge
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const InfoTile = ({ label, value, hint, accent }) => (
  <div
    className="rounded-xl p-3 border"
    style={{
      background: accent === 'rose' ? 'rgba(248,113,113,0.06)' : 'rgba(255,182,18,0.04)',
      borderColor: accent === 'rose' ? 'rgba(248,113,113,0.32)' : 'rgba(255,182,18,0.12)',
    }}
  >
    <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">{label}</div>
    <div className="mt-0.5 font-semibold text-sm sc-tabular">{value}</div>
    {hint ? <div className="text-[10px] text-[var(--sc-text-mute)] mt-1 leading-snug">{hint}</div> : null}
  </div>
);

export default CheatersSubpage;
